import React, { useState } from 'react';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Grid,
    Paper,
    ClickAwayListener
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MainCard from './cards/MainCard';

const GlobalDateRangeSelector = ({
    value,
    onChange,
    onCustomStartDateChange,
    onCustomEndDateChange,
    customStartDate,
    customEndDate,
    includeCustom = false,
    size = 'small',
    fullWidth = false,
    label = 'Date Range',
    sx = {},
    showCurrentRange = false,
    customRangeTitle = 'Select Custom Date Range',
    ...props
}) => {
    // State to control overlay visibility
    const [showCustomOverlay, setShowCustomOverlay] = useState(false);
    // Base date range options
    const baseDateRangeOptions = [
        { value: '6months', label: 'Past 6 Months' },
        { value: '12months', label: 'Past 12 Months' }
    ];

    // Add custom option if needed
    const dateRangeOptions = includeCustom
        ? [...baseDateRangeOptions, { value: 'custom', label: 'Custom Range' }]
        : baseDateRangeOptions;

    const handleDateRangeChange = (event) => {
        const newValue = event.target.value;

        // Show overlay when custom is selected
        if (newValue === 'custom') {
            setShowCustomOverlay(true);
        } else {
            setShowCustomOverlay(false);
        }

        // If switching to custom and no dates are set, set default values
        if (newValue === 'custom' && includeCustom && (!customStartDate || !customEndDate)) {
            const now = new Date();
            const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());

            // Call the custom date handlers if they exist
            if (onCustomStartDateChange) {
                onCustomStartDateChange({ target: { value: sixMonthsAgo.toISOString().split('T')[0] } });
            }
            if (onCustomEndDateChange) {
                onCustomEndDateChange({ target: { value: now.toISOString().split('T')[0] } });
            }
        }

        onChange(event);
    };

    const handleClickAway = () => {
        // Just hide the overlay, keep the custom selection
        setShowCustomOverlay(false);
    };

    const getCurrentRangeLabel = () => {
        const selectedOption = dateRangeOptions.find(opt => opt.value === value);
        return selectedOption ? selectedOption.label : 'Past 6 Months';
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box sx={{ width: '100%', position: 'relative', ...sx }}>
                {/* Main Date Range Selector */}
                <FormControl
                    variant="outlined"
                    size={size}
                    fullWidth={fullWidth}
                    {...props}
                >
                    <InputLabel id="date-range-label">{label}</InputLabel>
                    <Select
                        labelId="date-range-label"
                        value={value}
                        onChange={handleDateRangeChange}
                        label={label}
                        startAdornment={<CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                    >
                        {dateRangeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Custom Date Range Picker - Overlay */}
                {includeCustom && value === 'custom' && showCustomOverlay && (
                    <Paper
                        elevation={8}
                        sx={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            mt: 0.5,
                            p: 2,
                            zIndex: 1300,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                            {customRangeTitle}
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    fullWidth
                                    label="Start Date"
                                    type="date"
                                    value={customStartDate || ''}
                                    onChange={onCustomStartDateChange}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    size={size}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    to
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    fullWidth
                                    label="End Date"
                                    type="date"
                                    value={customEndDate || ''}
                                    onChange={onCustomEndDateChange}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    size={size}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                )}
            </Box>
        </ClickAwayListener>
    );
};

export default GlobalDateRangeSelector;